import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Badge,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  LinearProgress,
  Stack,
} from '@mui/material';
import { Grid } from '../components/common/TypedGrid';
import {
  Visibility,
  Assignment,
  LocalShipping,
  CheckCircle,
  Cancel,
  Search,
  FilterList,
  Download,
  Edit,
  Delete,
  Close,
  LocationOn,
  AccessTime,
  AttachMoney,
  Person,
  EditNote,
  Category,
  PriorityHigh,
  Inventory2,
} from '@mui/icons-material';
import { useAdmin } from '../contexts/AdminContext';
import {
  PageHeader,
  ResponsiveTable,
  RouteSummary,
  SectionCard,
  StatCard,
  StatusChip,
  TaskRouteMap,
  TaskRouteMapLegend,
} from '../components/common';
import type { ResponsiveColumn, RoutePoint } from '../components/common';
import { exportToCSV as exportToCSVShared } from '../utils/csvExport';
import adminService, {
  DeliveryTask,
  TaskStatusFilter,
  ACTIVE_TASK_STATUSES,
} from '../services/adminService';
import TaskLocationHistory from '../components/TaskLocationHistory';
import { useTaskLocationHistory } from '../hooks/useTaskLocationHistory';
import { db } from '../config/firebase';
import { collection, query, onSnapshot, orderBy, limit } from 'firebase/firestore';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`task-tabpanel-${index}`}
    aria-labelledby={`task-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

/** A "label above value" pair — the detail-dialog equivalent of a form field, read-only. */
const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
      {value || value === 0 ? value : <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>Not provided</Box>}
    </Typography>
  </Box>
);

/** One row of a compact "attributes" sidebar — icon, label, and a right-aligned value or chip. */
const AttributeRow: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ py: 1 }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary', minWidth: 0 }}>
      <Box sx={{ display: 'grid', placeItems: 'center', flexShrink: 0 }}>{icon}</Box>
      <Typography variant="body2">{label}</Typography>
    </Stack>
    <Box sx={{ minWidth: 0, textAlign: 'right' }}>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
          {value}
        </Typography>
      ) : (
        value
      )}
    </Box>
  </Stack>
);

const TaskManagement: React.FC = () => {
  const { refreshDashboardStats } = useAdmin();
  const [tasks, setTasks] = useState<DeliveryTask[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<DeliveryTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<DeliveryTask | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // The ninja's most recent GPS ping for the task currently open in the
  // detail dialog — feeds the live marker on the route map. Only fetches
  // while a task is actually selected; TaskLocationHistory (the audit
  // timeline further down this dialog) reads the same document
  // independently, which is one extra read on an admin-triggered, rarely-open
  // dialog rather than something worth plumbing through props for.
  const { history: taskLocationHistory } = useTaskLocationHistory(
    detailsOpen ? selectedTask?.id : undefined
  );

  // Issue #358: the list is served a page at a time instead of downloading the
  // whole tasks collection on every load. `cursor` is the Firestore document
  // the next page resumes after; `counts` come from server-side count queries
  // so the cards and tab badges stay exact while the table stays paged.
  const [cursor, setCursor] = useState<unknown | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [counts, setCounts] = useState({ all: 0, active: 0, completed: 0, cancelled: 0 });

  /**
   * The status set implied by the current tab and the status dropdown.
   *
   * Both used to be applied client-side to a fully downloaded collection.
   * Pushing them into the query is what makes a page meaningful: without it,
   * "page 1 of cancelled tasks" would be "the first 25 tasks, of which the
   * cancelled ones happen to be shown".
   */
  const activeStatuses = useMemo<TaskStatusFilter>(() => {
    const fromTab: TaskStatusFilter =
      tabValue === 1 ? ACTIVE_TASK_STATUSES
      : tabValue === 2 ? ['completed']
      : tabValue === 3 ? ['cancelled']
      : 'all';

    if (filterStatus === 'all') return fromTab;
    if (fromTab === 'all') return [filterStatus];
    // Dropdown narrows the tab; an impossible combination yields no rows,
    // which is the honest answer rather than silently ignoring one of them.
    return fromTab.includes(filterStatus) ? [filterStatus] : [];
  }, [tabValue, filterStatus]);

  // Export functionality
  const exportToCSV = (data: any[], filename: string) => {
    exportToCSVShared(data, filename, () =>
      setSnackbar({ open: true, message: 'No data to export', severity: 'error' })
    );
    if (data && data.length > 0) {
      setSnackbar({ open: true, message: `${filename} exported successfully!`, severity: 'success' });
    }
  };

  const handleExportTasks = async () => {
    // Issue #358: the table holds one page, so exporting what is on screen
    // would quietly produce a short file. The export fetches the full matching
    // set itself, in bounded pages.
    setExporting(true);
    let rows: DeliveryTask[];
    let truncated = false;
    try {
      const result = await adminService.getTasksForExport(activeStatuses);
      rows = result.rows;
      truncated = result.truncated;
    } catch (error) {
      console.error('Error preparing task export:', error);
      showSnackbar('Could not prepare the export. Please try again.', 'error');
      setExporting(false);
      return;
    } finally {
      setExporting(false);
    }

    if (truncated) {
      showSnackbar(
        `Export capped at ${rows.length.toLocaleString()} rows. Narrow the filter to export the rest.`,
        'error'
      );
    }

    const exportData = rows.map(task => {
      // For delivery tasks, packageType serves as the category (Food, Documents, Grocery, Parcel, etc.)
      // For service tasks, category is the service category
      const taskCategory = task.taskType === 'service'
        ? (task.category || 'N/A')
        : (task.packageType || task.category || 'N/A');

      // Handle "Scheduled For" for both delivery and service tasks
      let scheduledFor = 'N/A';
      if (task.scheduledFor) {
        scheduledFor = new Date(task.scheduledFor).toLocaleString();
      } else if (task.taskType === 'service' && task.urgency === 'scheduled' && task.preferredDate) {
        // For scheduled service tasks, combine preferredDate and preferredTime
        const dateStr = task.preferredDate instanceof Date
          ? task.preferredDate.toLocaleDateString()
          : new Date(task.preferredDate).toLocaleDateString();
        const timeStr = task.preferredTime || '';
        scheduledFor = timeStr ? `${dateStr} - ${timeStr}` : dateStr;
      }

      // Handle Payment Method with multiple fallback options
      // Check task.paymentMethod, paymentDetails.method, or infer from payment status
      const paymentMethod = task.paymentMethod ||
                           (task as any).paymentDetails?.method ||
                           (task as any).payment?.method ||
                           (task.paymentStatus === 'paid' ? 'online' : 'N/A');

      return {
        'Task ID': task.id,
        'Order ID': task.orderId || 'N/A',
        'Title': task.title || 'N/A',
        'Task Type': task.taskType || 'delivery',
        'Category': taskCategory,
        'Status': task.status,
        'Urgency': task.urgency || 'standard',
        'Weight (kg)': task.weight || task.packageWeight || 'N/A',
        'Customer ID': task.userId || 'N/A',
        'Assigned To': task.assignedTo || 'Unassigned',
        'Estimated Price (INR)': task.estimatedPrice || 0,
        'Final Price (INR)': task.finalPrice || task.estimatedPrice || 0,
        'Payment Status': task.paymentStatus || 'pending',
        'Payment Method': paymentMethod,
        'Rating': task.rating || 'N/A',
        'Created At': task.createdAt ? new Date(task.createdAt).toLocaleString() : 'N/A',
        'Updated At': task.updatedAt ? new Date(task.updatedAt).toLocaleString() : 'N/A',
        'Completed At': task.completedAt ? new Date(task.completedAt).toLocaleString() : 'N/A',
        'Scheduled For': scheduledFor,
        'Special Instructions': task.specialInstructions || 'None'
      };
    });

    exportToCSV(exportData, 'tasks_export');
  };

  useEffect(() => {
    // Issue #358: this listener used to watch the whole tasks collection, so
    // every write anywhere in the product streamed the entire collection to
    // the browser and triggered another full reload. It only needs to be a
    // change signal, so it watches the newest task and nothing else.
    const latestTaskQuery = query(
      collection(db, 'tasks'),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    let first = true;
    const unsubscribe = onSnapshot(latestTaskQuery, () => {
      if (first) {
        first = false; // the initial snapshot is not a change
        return;
      }
      console.log('📊 Tasks changed — refreshing the first page');
      reloadFirstPage();
    }, (error) => {
      console.error('Error listening to tasks:', error);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload from the first page whenever the server-side filter changes.
  useEffect(() => {
    reloadFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatuses]);

  useEffect(() => {
    filterTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, searchTerm]);

  const reloadFirstPage = async () => {
    setLoading(true);
    try {
      const [page, statusCounts] = await Promise.all([
        adminService.getTasksPage({ statuses: activeStatuses }),
        adminService.getTaskStatusCounts(),
      ]);
      setTasks(page.rows);
      setCursor(page.cursor);
      setHasMore(page.hasMore);
      setCounts(statusCounts);
    } catch (error) {
      console.error('Error loading tasks:', error);
      showSnackbar('Error loading tasks', 'error');
    } finally {
      setLoading(false);
    }
  };

  /** Kept for the handlers that refresh after a mutation. */
  const loadTasks = reloadFirstPage;

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await adminService.getTasksPage({ statuses: activeStatuses, cursor });
      setTasks(prev => [...prev, ...page.rows]);
      setCursor(page.cursor);
      setHasMore(page.hasMore);
    } catch (error) {
      console.error('Error loading more tasks:', error);
      showSnackbar('Could not load more tasks. Please try again.', 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  /**
   * Search within the rows that are loaded.
   *
   * Firestore has no substring search, so this cannot be pushed into the query
   * the way the status filters were. The previous behaviour only appeared to
   * search everything because the whole collection happened to be in memory.
   * Pasting an order id still finds the task wherever it is — that path goes
   * through an indexed equality lookup below.
   */
  const filterTasks = () => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      setFilteredTasks(tasks);
      return;
    }

    setFilteredTasks(tasks.filter(task =>
      (task.title && task.title.toLowerCase().includes(term)) ||
      (task.customerInfo?.name && task.customerInfo.name.toLowerCase().includes(term)) ||
      (task.serviceLocation?.contactDetails?.contactName && task.serviceLocation.contactDetails.contactName.toLowerCase().includes(term)) ||
      task.id.toLowerCase().includes(term) ||
      (task.orderId && task.orderId.toLowerCase().includes(term)) ||
      (task.packageType && task.packageType.toLowerCase().includes(term)) ||
      (task.serviceName && task.serviceName.toLowerCase().includes(term)) ||
      (task.category && task.category.toLowerCase().includes(term))
    ));
  };

  /**
   * Fetch an exact order-id match that is not in the loaded pages, so a support
   * agent pasting an order number is not told the task does not exist just
   * because it is older than the rows on screen.
   */
  const handleSearchAllOrders = async () => {
    const term = searchTerm.trim();
    if (!term) return;
    try {
      const matches = await adminService.findTasksByOrderId(term);
      if (matches.length === 0) {
        showSnackbar(`No task found with order ID "${term}".`, 'error');
        return;
      }
      // Merge into the loaded set so the row renders with everything else.
      setTasks(prev => {
        const known = new Set(prev.map(t => t.id));
        return [...matches.filter(m => !known.has(m.id)), ...prev];
      });
    } catch (error) {
      console.error('Error searching by order ID:', error);
      showSnackbar('Search failed. Please try again.', 'error');
    }
  };

  const handleUpdateTaskStatus = async (taskId: string, newStatus: string) => {
    if (updatingStatus) return;

    const confirmMessage =
      newStatus === 'cancelled'
        ? 'Cancel this task? Any locked customer funds will be refunded.'
        : newStatus === 'completed'
        ? 'Mark this task completed? This will pay out the ninja and charge the customer.'
        : null;
    if (confirmMessage && !window.confirm(confirmMessage)) {
      return;
    }

    setUpdatingStatus(true);
    try {
      const ok = await adminService.updateTaskStatus(taskId, newStatus);
      if (!ok) {
        showSnackbar('Error updating task status', 'error');
        return;
      }
      await loadTasks();
      await refreshDashboardStats();
      showSnackbar(`Task ${newStatus} successfully`, 'success');
      setDetailsOpen(false);
    } catch (error) {
      console.error('Error updating task status:', error);
      showSnackbar('Error updating task status', 'error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'standard': return 'default';
      case 'express': return 'primary';
      case 'urgent': return 'warning';
      case 'immediate': return 'error';
      case 'scheduled': return 'info';
      case 'flexible': return 'success';
      default: return 'default';
    }
  };

  // Issue #358: counts come from server-side count queries, not from
  // `.filter().length` over a downloaded collection, so they stay exact even
  // though the table only holds the pages that have been loaded.
  const tabCounts = counts;

  return (
    <Box>
      <PageHeader title="Tasks" subtitle="Deliveries and services booked through the app" />

      <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard label="Total Tasks" value={tabCounts.all} icon={<Assignment />} tone="primary" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Active" value={tabCounts.active} icon={<LocalShipping />} tone="info" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Completed" value={tabCounts.completed} icon={<CheckCircle />} tone="success" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Cancelled" value={tabCounts.cancelled} icon={<Cancel />} tone="error" />
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search loaded tasks..."
              helperText="Searches the rows loaded below. Press Enter to look up an exact order ID."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearchAllOrders(); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <Button
              startIcon={<Download />}
              variant="outlined"
              onClick={handleExportTasks}
              disabled={exporting}
            >
              {exporting ? 'Preparing export…' : 'Export'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab 
              label={
                <Badge badgeContent={tabCounts.all} color="primary" showZero>
                  All Tasks
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={tabCounts.active} color="info" showZero>
                  Active
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={tabCounts.completed} color="success" showZero>
                  Completed
                </Badge>
              } 
            />
            <Tab 
              label={
                <Badge badgeContent={tabCounts.cancelled} color="error" showZero>
                  Cancelled
                </Badge>
              } 
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <TaskTable 
            tasks={filteredTasks} 
            onViewDetails={(task) => { setSelectedTask(task); setDetailsOpen(true); }}
            onUpdateStatus={handleUpdateTaskStatus}
            loading={loading}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <TaskTable 
            tasks={filteredTasks} 
            onViewDetails={(task) => { setSelectedTask(task); setDetailsOpen(true); }}
            onUpdateStatus={handleUpdateTaskStatus}
            loading={loading}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <TaskTable 
            tasks={filteredTasks} 
            onViewDetails={(task) => { setSelectedTask(task); setDetailsOpen(true); }}
            onUpdateStatus={handleUpdateTaskStatus}
            loading={loading}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <TaskTable 
            tasks={filteredTasks} 
            onViewDetails={(task) => { setSelectedTask(task); setDetailsOpen(true); }}
            onUpdateStatus={handleUpdateTaskStatus}
            loading={loading}
          />
        </TabPanel>

        {/* Issue #358: the table holds loaded pages, not the whole collection. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {`Showing ${filteredTasks.length.toLocaleString()} of ${tabCounts[
              tabValue === 1 ? 'active' : tabValue === 2 ? 'completed' : tabValue === 3 ? 'cancelled' : 'all'
            ].toLocaleString()}`}
          </Typography>
          {hasMore && (
            <Button onClick={handleLoadMore} disabled={loadingMore} variant="outlined" size="small">
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          )}
        </Box>
      </Card>

      {/* Task Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            py: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>
              <Assignment fontSize="small" />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1, lineHeight: 1 }} display="block">
                Task Details
              </Typography>
              <Typography variant="h6" fontWeight={700} noWrap>
                {selectedTask?.orderId || 'N/A'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }} noWrap display="block">
                Task ID: {selectedTask?.id}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={() => setDetailsOpen(false)}
            sx={{ color: 'white', flexShrink: 0 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedTask && (
            <Grid container spacing={3}>
              {/* Main content */}
              <Grid item xs={12} md={8}>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {selectedTask.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {selectedTask.orderId} • {selectedTask.taskType === 'service' ? 'Service' : 'Delivery'}
                    </Typography>
                  </Box>

              {/* Customer Information */}
              {/* Issue #210: Use customerInfo.name which has fallbacks built in adminService.getAllTasks() */}
              <SectionCard title="Contact Information" icon={<Person fontSize="small" />} tone="secondary">
                <Grid container spacing={2.5}>
                  {selectedTask.taskType === 'service' ? (
                    <>
                      {/* Issue #210: Use customerInfo.name as primary (built with fallbacks), then serviceLocation.contactDetails.contactName */}
                      <Grid item xs={6} sm={4}>
                        <Field label="Name" value={selectedTask.customerInfo?.name || selectedTask.serviceLocation?.contactDetails?.contactName || 'Unknown'} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Field label="Phone" value={selectedTask.customerInfo?.phone || selectedTask.serviceLocation?.contactDetails?.phone} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Field label="Alternate Phone" value={selectedTask.serviceLocation?.contactDetails?.alternatePhone} />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={6} sm={4}>
                        <Field label="Name" value={selectedTask.customerInfo?.name || 'Unknown Customer'} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Field label="Email" value={selectedTask.customerInfo?.email} />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Field label="Phone" value={selectedTask.customerInfo?.phone} />
                      </Grid>
                    </>
                  )}
                </Grid>
              </SectionCard>

              {/* Ninja Information */}
              {selectedTask.ninjaInfo && (
                <SectionCard title="Ninja Information" icon={<LocalShipping fontSize="small" />} tone="success">
                  <Grid container spacing={2.5}>
                    <Grid item xs={6} sm={4}>
                      <Field label="Name" value={selectedTask.ninjaInfo?.name || 'Unknown Ninja'} />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Field label="Phone" value={selectedTask.ninjaInfo?.phone} />
                    </Grid>
                  </Grid>
                </SectionCard>
              )}

              {/* Route & Cost — full width, so the map has room to be useful */}
              {selectedTask.taskType === 'delivery' ? (
                <Stack spacing={3}>
                  <SectionCard title="Route" subtitle="Pickup and drop, with the ninja's last known position">
                    {(() => {
                      const points = selectedTask.points || [];
                      const pickupPoint =
                        points.find((p: any) => p.pointType === 'pickup') || points[0];
                      const dropPoint =
                        points.find((p: any) => p.pointType === 'drop') ||
                        points[points.length - 1];

                      const toRoutePoint = (point: any): RoutePoint | null =>
                        point?.coordinates?.latitude && point?.coordinates?.longitude
                          ? {
                              lat: point.coordinates.latitude,
                              lng: point.coordinates.longitude,
                              label: point.address?.city || point.address?.area || 'Location',
                              detail: point.address?.fullAddress,
                            }
                          : null;

                      const pickupRoutePoint = pickupPoint ? toRoutePoint(pickupPoint) : null;
                      const dropRoutePoint = dropPoint ? toRoutePoint(dropPoint) : null;

                      const lastPing = taskLocationHistory[0];
                      const ninjaLocation =
                        selectedTask.status === 'in_progress' && lastPing?.location
                          ? { lat: lastPing.location.latitude, lng: lastPing.location.longitude }
                          : null;

                      return (
                        <>
                          {pickupPoint && dropPoint && (
                            <Box sx={{ mb: 2.5 }}>
                              <RouteSummary
                                originLabel={
                                  pickupPoint.address?.city || pickupPoint.address?.area || 'Pickup'
                                }
                                destinationLabel={
                                  dropPoint.address?.city || dropPoint.address?.area || 'Drop'
                                }
                                progress={
                                  selectedTask.status === 'assigned'
                                    ? 0.1
                                    : selectedTask.status === 'in_progress'
                                    ? 0.6
                                    : selectedTask.status === 'completed'
                                    ? 1
                                    : undefined
                                }
                              />
                            </Box>
                          )}

                          <TaskRouteMap
                            pickup={pickupRoutePoint}
                            drop={dropRoutePoint}
                            ninjaLocation={ninjaLocation}
                          />
                          <TaskRouteMapLegend showNinja={Boolean(ninjaLocation)} />

                          {selectedTask.routeData && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                              {selectedTask.routeData.totalDistance.toFixed(1)} km total •{' '}
                              {selectedTask.routeData.totalTime} min estimated
                            </Typography>
                          )}

                          {points.length > 0 ? (
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                              {points.map((point: any, index: number) => {
                                const isPickup = point.pointType === 'pickup';
                                const isDrop =
                                  point.pointType === 'drop' ||
                                  (!point.pointType && index === points.length - 1);
                                const pointLabel = String.fromCharCode(65 + index);
                                const pointType = isPickup ? 'Pickup' : isDrop ? 'Drop' : 'Waypoint';
                                const distance = selectedTask.routeData?.pointDistances?.[index];
                                const time = selectedTask.routeData?.pointTimes?.[index];

                                return (
                                  <Grid item xs={12} md={6} key={index}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 2,
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: 'action.hover',
                                        height: '100%',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 40,
                                          height: 40,
                                          borderRadius: '50%',
                                          display: 'grid',
                                          placeItems: 'center',
                                          fontWeight: 700,
                                          flexShrink: 0,
                                          bgcolor: isPickup
                                            ? 'error.main'
                                            : isDrop
                                            ? 'success.main'
                                            : 'grey.500',
                                          color: '#fff',
                                        }}
                                      >
                                        {pointLabel}
                                      </Box>
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600}>
                                          Point {pointLabel} ({pointType})
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block', mt: 0.25 }}
                                        >
                                          {point.address?.fullAddress || 'No address provided'}
                                        </Typography>
                                        {point.contactDetails?.phone && (
                                          <Typography
                                            variant="caption"
                                            sx={{ display: 'block', mt: 0.5 }}
                                          >
                                            📞 {point.contactDetails.phone}
                                            {point.contactDetails.contactName &&
                                              ` • ${point.contactDetails.contactName}`}
                                          </Typography>
                                        )}
                                        {distance !== undefined && time !== undefined && (
                                          <Typography
                                            variant="caption"
                                            color="primary.main"
                                            fontWeight={600}
                                            sx={{ display: 'block', mt: 0.5 }}
                                          >
                                            {distance.toFixed(1)} km • {time}m to next stop
                                          </Typography>
                                        )}
                                      </Box>
                                      {point.contactDetails?.phone && (
                                        <IconButton
                                          component="a"
                                          href={`tel:${point.contactDetails.phone}`}
                                          size="small"
                                          color="success"
                                          aria-label={`Call ${point.contactDetails.contactName || pointType}`}
                                        >
                                          📞
                                        </IconButton>
                                      )}
                                    </Box>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No location information available
                            </Typography>
                          )}
                        </>
                      );
                    })()}
                  </SectionCard>

                  {selectedTask.invoiceData && (
                    <SectionCard title="Cost Breakdown">
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {selectedTask.invoiceData.breakdown?.map((item, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              py: 1,
                              px: 1,
                              borderRadius: 2,
                              bgcolor: index % 2 === 0 ? 'action.hover' : 'transparent',
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                {item.item}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.description}
                              </Typography>
                            </Box>
                            <Typography variant="body1" fontWeight={700} color="success.dark">
                              ₹{item.amount}
                            </Typography>
                          </Box>
                        ))}

                        <Divider sx={{ borderWidth: 2 }} />

                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 2,
                            px: 2,
                            borderRadius: 2,
                            bgcolor: 'success.main',
                          }}
                        >
                          <Typography variant="h6" fontWeight={700} sx={{ color: '#fff' }}>
                            Total Amount:
                          </Typography>
                          <Typography variant="h5" fontWeight={700} sx={{ color: '#fff' }}>
                            ₹{selectedTask.invoiceData.total}
                          </Typography>
                        </Box>
                      </Box>
                    </SectionCard>
                  )}
                </Stack>
              ) : (
/* Location for Service Tasks — a single point, so the map has no
                   route to draw, just the one pin. */
                <SectionCard title="Location">
                  {selectedTask.serviceLocation ? (
                    <>
                      <TaskRouteMap
                        pickup={
                          selectedTask.serviceLocation.coordinates?.latitude &&
                          selectedTask.serviceLocation.coordinates?.longitude
                            ? {
                                lat: selectedTask.serviceLocation.coordinates.latitude,
                                lng: selectedTask.serviceLocation.coordinates.longitude,
                                label:
                                  selectedTask.serviceLocation.address?.city ||
                                  selectedTask.serviceLocation.address?.area ||
                                  'Service location',
                              }
                            : null
                        }
                        drop={null}
                        height={240}
                      />

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {selectedTask.serviceLocation.address?.fullAddress || 'No address provided'}
                        </Typography>
                        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                          {[
                            ['House/Unit', selectedTask.serviceLocation.address?.houseNumber],
                            ['Street', selectedTask.serviceLocation.address?.street],
                            ['Area', selectedTask.serviceLocation.address?.area],
                            ['City', selectedTask.serviceLocation.address?.city],
                            ['Pincode', selectedTask.serviceLocation.address?.pincode],
                          ].map(([label, value]) => (
                            <Grid item xs={6} md={4} key={label}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {label}
                              </Typography>
                              <Typography variant="body2">{value || 'N/A'}</Typography>
                            </Grid>
                          ))}
                        </Grid>
                        {selectedTask.serviceLocation.address?.landmark && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1.5, fontStyle: 'italic' }}
                          >
                            Landmark: {selectedTask.serviceLocation.address.landmark}
                          </Typography>
                        )}
                      </Box>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No location information available
                    </Typography>
                  )}
                </SectionCard>
              )}

              {/* Special Instructions */}
              {selectedTask.specialInstructions && (
                <SectionCard title="Special Instructions" icon={<EditNote fontSize="small" />} tone="warning">
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    {selectedTask.specialInstructions}
                  </Typography>
                </SectionCard>
              )}

              {/* Location History Component */}
              <TaskLocationHistory taskId={selectedTask.id} />
                </Stack>
              </Grid>

              {/* Attributes sidebar */}
              <Grid item xs={12} md={4}>
                <SectionCard title="Attributes" icon={<Assignment fontSize="small" />} tone="primary">
                  <Stack divider={<Divider />}>
                    <AttributeRow
                      icon={<CheckCircle fontSize="small" />}
                      label="Status"
                      value={<StatusChip status={selectedTask.status} />}
                    />
                    <AttributeRow
                      icon={<PriorityHigh fontSize="small" />}
                      label="Urgency"
                      value={
                        <Chip
                          label={selectedTask.urgency || 'standard'}
                          color={getUrgencyColor(selectedTask.urgency) as any}
                          size="small"
                        />
                      }
                    />
                    <AttributeRow
                      icon={<Category fontSize="small" />}
                      label="Task Type"
                      value={selectedTask.taskType || 'delivery'}
                    />
                    {selectedTask.taskType === 'delivery' ? (
                      <>
                        <AttributeRow
                          icon={<Inventory2 fontSize="small" />}
                          label="Package Type"
                          value={selectedTask.packageType || 'Not provided'}
                        />
                        <AttributeRow
                          icon={<Inventory2 fontSize="small" />}
                          label="Weight"
                          value={selectedTask.weight ? `${selectedTask.weight} kg` : 'Not provided'}
                        />
                      </>
                    ) : (
                      <>
                        <AttributeRow
                          icon={<Category fontSize="small" />}
                          label="Service"
                          value={selectedTask.serviceName || 'Not provided'}
                        />
                        <AttributeRow
                          icon={<Category fontSize="small" />}
                          label="Category"
                          value={selectedTask.category || 'Not provided'}
                        />
                      </>
                    )}
                    <AttributeRow
                      icon={<AttachMoney fontSize="small" />}
                      label="Estimated Price"
                      value={`₹${selectedTask.estimatedPrice}`}
                    />
                    <AttributeRow
                      icon={<AccessTime fontSize="small" />}
                      label="Created"
                      value={new Date(selectedTask.createdAt).toLocaleDateString()}
                    />
                    {selectedTask.status === 'completed' && selectedTask.completedAt && (
                      <AttributeRow
                        icon={<CheckCircle fontSize="small" />}
                        label="Completed At"
                        value={new Date(selectedTask.completedAt).toLocaleDateString()}
                      />
                    )}
                  </Stack>
                </SectionCard>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          py: 2, 
          backgroundColor: 'action.hover',
          justifyContent: 'flex-end',
          gap: 1
        }}>
          {selectedTask && ['pending', 'assigned', 'in_progress'].includes(selectedTask.status) && (
            <>
              <Button 
                onClick={() => selectedTask && handleUpdateTaskStatus(selectedTask.id, 'cancelled')}
                color="error"
                variant="outlined"
                startIcon={<Cancel />}
                size="small"
                disabled={updatingStatus}
              >
                Cancel Task
              </Button>
              {selectedTask.status === 'in_progress' && (
                <Button 
                  onClick={() => selectedTask && handleUpdateTaskStatus(selectedTask.id, 'completed')}
                  color="success"
                  variant="contained"
                  startIcon={<CheckCircle />}
                  size="small"
                  disabled={updatingStatus}
                >
                  Mark Completed
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

interface TaskTableProps {
  tasks: DeliveryTask[];
  onViewDetails: (task: DeliveryTask) => void;
  onUpdateStatus: (taskId: string, status: string) => void;
  loading: boolean;
}

const TaskTable: React.FC<TaskTableProps> = ({ 
  tasks, 
  onViewDetails, 
  onUpdateStatus, 
  loading
}) => {
  /**
   * Name and phone are derived differently for service and delivery tasks:
   * a service task carries them on the service location, with customerInfo as
   * the fallback. Lifted out of the cell so the desktop table and the phone
   * card cannot disagree.
   */
  const customerOf = (task: DeliveryTask) => {
    const name =
      task.taskType === 'service'
        ? task.serviceLocation?.contactDetails?.contactName ||
          task.customerInfo?.name ||
          'Unknown Customer'
        : task.customerInfo?.name || 'Unknown Customer';

    const rawPhone =
      task.taskType === 'service'
        ? task.serviceLocation?.contactDetails?.phone || task.customerInfo?.phone
        : task.customerInfo?.phone;

    return { name, phone: rawPhone ? String(rawPhone) : 'No phone' };
  };

  /** How far through its pickup→drop leg a task is, for the route strip. */
  const routeProgress = (status: DeliveryTask['status']): number | undefined => {
    switch (status) {
      case 'assigned':
        return 0.1;
      case 'in_progress':
        return 0.6;
      case 'completed':
        return 1;
      default:
        return undefined; // pending / cancelled: no leg in progress to show
    }
  };

  /** The two endpoints of a delivery's route, or null if there aren't two. */
  const routeEndpoints = (task: DeliveryTask) => {
    if (task.taskType !== 'delivery' || !task.points || task.points.length < 2) return null;
    const pickup = task.points.find((p: any) => p.pointType === 'pickup') || task.points[0];
    const drop =
      task.points.find((p: any) => p.pointType === 'drop') || task.points[task.points.length - 1];
    return { pickup, drop };
  };

  const columns: ResponsiveColumn<DeliveryTask>[] = [
    {
      key: 'task',
      header: 'Task',
      mobile: 'title',
      width: 260,
      render: (task) => {
        const route = routeEndpoints(task);
        return (
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {task.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Order ID: {task.orderId} • {task.taskType === 'service' ? '🔧 Service' : '📦 Delivery'}
            </Typography>
            {route && (
              <Box sx={{ mt: 1, maxWidth: 260 }}>
                <RouteSummary
                  compact
                  originLabel={route.pickup.address?.city || route.pickup.address?.area || 'Pickup'}
                  destinationLabel={route.drop.address?.city || route.drop.address?.area || 'Drop'}
                  progress={routeProgress(task.status)}
                />
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (task) => {
        const { name, phone } = customerOf(task);
        return (
          <>
            <Typography variant="body2">{name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {phone}
            </Typography>
          </>
        );
      },
    },
    {
      key: 'package',
      header: 'Package',
      render: (task) =>
        task.taskType === 'service' ? (
          <>
            <Typography variant="body2">
              {task.serviceName || task.category || 'Service'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Category: {task.category || 'N/A'} • {task.urgency}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="body2">
              {task.packageType || task.category || 'Delivery'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {task.weight ? `${task.weight} kg • ` : ''}
              {task.urgency}
            </Typography>
          </>
        ),
    },
    {
      key: 'price',
      header: 'Price',
      render: (task) => (
        <Typography variant="body2" fontWeight={600}>
          ₹{task.estimatedPrice}
        </Typography>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (task) => <StatusChip status={task.status} />,
    },
    {
      key: 'created',
      header: 'Created',
      render: (task) => (
        <>
          <Typography variant="body2">
            {new Date(task.createdAt).toLocaleDateString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(task.createdAt).toLocaleTimeString()}
          </Typography>
        </>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'actions',
      render: (task) => (
        <IconButton
          onClick={() => onViewDetails(task)}
          size="small"
          aria-label="View task details"
        >
          <Visibility />
        </IconButton>
      ),
    },
  ];

  return (
    <ResponsiveTable
      columns={columns}
      rows={tasks}
      rowKey={(task) => task.id}
      loading={loading}
      onRowClick={onViewDetails}
      aria-label="Tasks"
      emptyTitle="No tasks found"
      emptyDescription="Tasks appear here once customers start booking. Try a different filter if you expected to see some."
    />
  );
};

export default TaskManagement;